/* ============================================================================
   MATRIZ IPERC DEL OPERADOR — ANDES

   Digitalizada de `IPER_SMS_-_Ver__01.xlsx`, hojas «Probabilidad»,
   «Severidad», «Matriz de Riesgos» y «Lista», y del Manual
   `MAN-SIG-SMS-001 Rev. 01`.

   ══════════════════════════════════════════════════════════════════════════
   ESTA MATRIZ NO SE INVENTA NI SE AJUSTA

   Es la matriz **aprobada** del operador. Los 25 niveles y sus clasificaciones
   están copiados uno por uno del archivo, no derivados de una fórmula.

   Se hizo así a propósito: una fórmula que «casi» reproduce la tabla es peor
   que la tabla, porque el día que difiera nadie lo va a notar. Y cambiar
   cualquier valor de acá es modificar un documento aprobado — trámite ante la
   Gerencia SIG y ante la autoridad, no una decisión de programación.

   Si la matriz cambia en una revisión del manual, se actualiza ACÁ y se sube
   la revisión declarada abajo.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

/** De qué documento salió esto. Viaja en los informes. */
export const PROCEDENCIA = {
  manual: "MAN-SIG-SMS-001 Manual SMS Rev. 01",
  matriz: "IPER SMS Ver. 01",
  operador: "ANDES",
  aprobadoPor: "Gerencia SIG",
  digitalizado: "2026-08-31",
  nota: "Matriz transcrita del documento aprobado. No es una interpretación: "
    + "los 25 niveles están copiados uno por uno.",
};

/**
 * Probabilidad. Valor numérico de 1 a 5.
 *
 * Ojo con el orden: en la matriz del operador **5 es lo más frecuente**, al
 * revés de lo que usan otras escalas. Invertirlo daría el riesgo opuesto.
 */
export const PROBABILIDAD = [
  { valor: 5, nombre: "Frecuente", criterio: "El peligro se ha materializado continuamente." },
  { valor: 4, nombre: "Probable", criterio: "El peligro se ha materializado varias veces." },
  { valor: 3, nombre: "Remoto", criterio: "Se sabe que el peligro se ha materializado alguna vez." },
  { valor: 2, nombre: "Raro", criterio: "Se sabe que el peligro se materializó en el sector." },
  { valor: 1, nombre: "Improbable", criterio: "Puede ocurrir solo en circunstancias excepcionales." },
];

/**
 * Severidad. Letra de A a E.
 *
 * También al revés de lo intuitivo: **A es lo más grave**. Un desarrollador
 * que asuma que A es lo más leve produce una matriz espejada, y eso no da
 * ningún error.
 */
export const SEVERIDAD = [
  { valor: "A", nombre: "Catastrófico", criterio: "Un muerto o más. Destrucción de la aeronave." },
  { valor: "B", nombre: "Crítico", criterio: "Lesiones o enfermedades graves o irreversibles." },
  { valor: "C", nombre: "Mayor", criterio: "Lesiones o enfermedades con incapacidad temporal." },
  { valor: "D", nombre: "Significativo", criterio: "Peligros que puedan generar incidentes." },
  { valor: "E", nombre: "Menor", criterio: "Valor de reposición o impacto menor." },
];

/**
 * Los 25 niveles, copiados de la hoja «Lista».
 *
 * La clave es `<probabilidad><severidad>`: `3B` es probabilidad 3 con
 * severidad B.
 */
export const NIVELES = {
  "1E": "Aceptable", "2E": "Aceptable", "1D": "Aceptable",
  "3E": "Intermedio", "4E": "Intermedio", "5E": "Intermedio",
  "2D": "Intermedio", "3D": "Intermedio", "1C": "Intermedio", "2C": "Intermedio",
  "1B": "Importante", "5D": "Importante", "4D": "Importante",
  "3C": "Importante", "2B": "Importante", "1A": "Importante",
  "4C": "Inaceptable", "3B": "Inaceptable", "2A": "Inaceptable",
  "5C": "Totalmente Inaceptable", "4B": "Totalmente Inaceptable",
  "5B": "Totalmente Inaceptable", "3A": "Totalmente Inaceptable",
  "4A": "Totalmente Inaceptable", "5A": "Totalmente Inaceptable",
};

/**
 * Qué hacer con cada nivel, y si el vuelo puede salir.
 *
 * `puedeOperar: false` no significa «la aplicación lo prohíbe» — ninguna
 * aplicación decide eso. Significa que según la matriz aprobada del operador,
 * ese nivel **requiere una decisión formal antes de operar**, y la aplicación
 * lo dice.
 */
export const TRATAMIENTO = {
  Aceptable: {
    color: "#4CAF7D",
    accion: "Aceptable. Seguir con los controles existentes.",
    requiereMitigacion: false,
    puedeOperar: true,
  },
  Intermedio: {
    color: "#FFD23F",
    accion: "Aceptable con seguimiento. Revisar la eficacia de las barreras.",
    requiereMitigacion: false,
    puedeOperar: true,
  },
  Importante: {
    color: "#FF8C42",
    accion: "Requiere medidas de mitigación adicionales antes de operar.",
    requiereMitigacion: true,
    puedeOperar: true,
  },
  Inaceptable: {
    color: "#E4573D",
    accion: "No operar hasta reducir el riesgo. Requiere autorización de la Gerencia SIG.",
    requiereMitigacion: true,
    puedeOperar: false,
  },
  "Totalmente Inaceptable": {
    color: "#B3261E",
    accion: "No operar. Detener la actividad hasta rediseñar los controles.",
    requiereMitigacion: true,
    puedeOperar: false,
  },
};

/** Áreas operacionales del alcance del manual (capítulo 1). */
export const AREAS = [
  ["FLT", "Operaciones de Vuelo"],
  ["DSP", "Control Operacional y Despacho"],
  ["MNT", "Mantenimiento de Aeronaves"],
  ["QA", "Control de Calidad"],
  ["IPC", "Ingeniería, Planificación y Control"],
  ["GRH", "Manipulación en Tierra"],
  ["LOG", "Logística, Compras y Distribución"],
  ["SEC", "Security"],
  ["SIG", "Sistema Integrado de Gestión"],
];

/** Los tres métodos de identificación de peligros (sección 4.2). */
export const METODOS = [
  ["reactivo", "Reactivo", "Eventos que ya ocurrieron: reportes, incidentes, accidentes."],
  ["proactivo", "Proactivo", "Actividades diarias: reportes, inspecciones, auditorías."],
  ["predictivo", "Predictivo", "Data de vuelo rutinaria capturada en tiempo real."],
];

const num = (v) => (Number.isFinite(v) ? v : null);

/** La clave de un par probabilidad-severidad. */
export function claveDe(probabilidad, severidad) {
  const p = num(probabilidad);
  const s = typeof severidad === "string" ? severidad.trim().toUpperCase() : null;
  if (p === null || !Number.isInteger(p) || p < 1 || p > 5) return null;
  if (!s || !SEVERIDAD.some((x) => x.valor === s)) return null;
  return `${p}${s}`;
}

/**
 * Evalúa un riesgo según la matriz aprobada.
 *
 * Devuelve `null` si el par no es válido. NO se elige un nivel por defecto:
 * un riesgo mal cargado que aparezca como «Aceptable» es exactamente el
 * problema que la matriz existe para evitar.
 */
export function evaluar(probabilidad, severidad) {
  const clave = claveDe(probabilidad, severidad);
  if (!clave) return null;

  const nivel = NIVELES[clave];
  if (!nivel) return null;

  const p = PROBABILIDAD.find((x) => x.valor === Number(clave[0]));
  const s = SEVERIDAD.find((x) => x.valor === clave[1]);

  return {
    clave,
    nivel,
    probabilidad: p,
    severidad: s,
    ...TRATAMIENTO[nivel],
    procedencia: PROCEDENCIA,
  };
}

/**
 * Compara dos evaluaciones: ¿la mitigación bajó el riesgo?
 *
 * El manual (sección de garantía de la seguridad) exige validar la eficacia de
 * las acciones. Esto responde si el riesgo residual es realmente menor.
 */
export const ORDEN_NIVELES = [
  "Aceptable", "Intermedio", "Importante", "Inaceptable", "Totalmente Inaceptable",
];

export function comparar(inicial, residual) {
  const a = ORDEN_NIVELES.indexOf(inicial?.nivel ?? inicial);
  const b = ORDEN_NIVELES.indexOf(residual?.nivel ?? residual);
  if (a < 0 || b < 0) return null;

  return {
    bajo: b < a,
    subio: b > a,
    igual: b === a,
    escalones: a - b,
    /* Que baje no alcanza: el manual pide que quede en un nivel operable. Una
       mitigación que lleva de «Totalmente Inaceptable» a «Inaceptable» bajó y
       sigue sin poder operar. */
    quedaOperable: TRATAMIENTO[ORDEN_NIVELES[b]]?.puedeOperar === true,
  };
}
